import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { ErrorCode } from '@common/http/enums/error-code.enum';
import { CollabSessionErrorEvent } from '@common/sockets/types/collab-session-error-event.type';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { ImagesFilter, SnippetFileFilter } from '@portal-core/project-files/constants/file-filters.constants';
import { ProjectFileType } from '@portal-core/project-files/enums/project-file-type.enum';
import { ProjectFilesService } from '@portal-core/project-files/services/project-files.service';
import { CollabFileTextEditorComponent } from '@portal-core/text-editor/components/collab-file-text-editor/collab-file-text-editor.component';
import { TextEditorService } from '@portal-core/text-editor/services/text-editor.service';
import { JoinedLoadingState, joinLoadingStates, LoadingState } from '@portal-core/util/loading-state';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'mc-project-files-tree-item-preview',
  templateUrl: './project-files-tree-item-preview.component.html',
  styleUrls: ['./project-files-tree-item-preview.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectFilesTreeItemPreviewComponent implements OnInit {
  @Input() commitId: string;
  @Input() projectId: number;
  @Input() defaultPath: string;
  @Input() branchName: string;
  @Input() treeFileFilter?: string;
  @Input() licenseUser?: LicenseUser;
  @Input() projectLanguage?: string;
  @Input() initialFilePath?: string;
  @Input() usingWorkspaceTree?: boolean;

  ProjectFileType: typeof ProjectFileType = ProjectFileType;

  collabType: CollabFileType = CollabFileType.Workspace;
  fileType: ProjectFileType;
  fileTypeLabel: string;
  loadingState: JoinedLoadingState<ErrorCode | string>;
  previewLoadingState: LoadingState<ErrorCode> = new LoadingState<ErrorCode>();
  previewSrc: string;
  snippetPath$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  stylesheetLoadingState: LoadingState<string> = new LoadingState();

  // For parent to use
  public selectedFilePath: string;
  @ViewChild('snippetPreviewEditor') snippetPreviewEditor: CollabFileTextEditorComponent;

  constructor(private errorService: ErrorService, private projectFilesService: ProjectFilesService, private textEditorService: TextEditorService) {
    this.loadingState = joinLoadingStates<ErrorCode | string>(this.previewLoadingState, this.stylesheetLoadingState, {
      errorStates: [this.previewLoadingState]
    });
  }

  ngOnInit() {
    if (this.treeFileFilter === ImagesFilter) {
      this.fileTypeLabel = 'Images';
    } else if (this.treeFileFilter === SnippetFileFilter) {
      this.fileTypeLabel = 'Snippets';
    } else {
      this.fileTypeLabel = 'Files';
    }

    if (this.initialFilePath) {
      this.onProjectFileSelected({ path: this.initialFilePath });
    }
  }

  /** Updates the preview image and enables the save button */
  onProjectFileSelected(fileNode: { path: string }) {
    const newSrc = this.projectFilesService.getProjectFileRawPath(this.projectId, this.commitId, fileNode.path);

    if (newSrc === this.previewSrc) {
      return;
    }

    this.previewLoadingState.update(true);
    // Reset the stylesheet loading state
    this.stylesheetLoadingState.update(false);

    this.fileType = this.projectFilesService.getFileType(fileNode.path);
    if (this.fileType === ProjectFileType.Flare) {
      this.snippetPath$.next(fileNode.path);
    }

    this.selectedFilePath = fileNode.path;
    this.previewSrc = newSrc;
  }

  onCollabDocLoaded() {
    this.previewLoadingState.update(false);
  }

  onCollabDocStylesheetLoad() {
    this.stylesheetLoadingState.update(true);
  }

  onCollabDocStylesheetLoaded(error: Error) {
    this.stylesheetLoadingState.update(false, error ? 'There was a problem loading the stylesheet.' : null, this.errorService.getErrorMessages(error));
  }

  onImageLoaded() {
    this.previewLoadingState.update(false);
  }

  onPreviewError(error: boolean | CollabSessionErrorEvent) {
    if (error) {
      this.previewLoadingState.update(false, ErrorCode.ProjectFileNotFoundError);
    }
  }
}
